import CONFIG from "../config";

const DS_BASE = "https://api.dexscreener.com/latest/dex";
const DS_API = "https://api.dexscreener.com";
const DP_BASE = "https://api.dexpaprika.com/networks/solana";

// ─── DexScreener: token pairs (liquidity) ───
// token-pairs is at /token-pairs/v1/... (not under /latest/dex); pair endpoint omits liquidity for pumpfun
async function fetchTokenPairsLiquidity() {
  try {
    const res = await fetch(`${DS_API}/token-pairs/v1/solana/${CONFIG.TOKEN_CA}`);
    const pairs = await res.json();
    if (!Array.isArray(pairs) || pairs.length === 0) return 0;
    const withLiq = pairs
      .map((x) => ({ liq: Number(x.liquidity?.usd ?? x.liquidity ?? 0) || 0, pair: x }))
      .filter((x) => x.liq > 0);
    if (withLiq.length === 0) return 0;
    const our = withLiq.find((x) => (x.pair.pairAddress ?? x.pair.pair_address) === CONFIG.PAIR_ADDRESS);
    const best = withLiq.reduce((a, b) => (a.liq >= b.liq ? a : b));
    return our ? our.liq : best.liq;
  } catch {
    return 0;
  }
}

// ─── DexScreener: pair data ─────────────────────────
export async function fetchPairData() {
  const res = await fetch(`${DS_BASE}/pairs/solana/${CONFIG.PAIR_ADDRESS}`);
  const data = await res.json();
  const p = data.pair ?? data.pairs?.[0];
  if (!p) return null;

  let liquidityUsd = Number(p.liquidity?.usd ?? p.liquidity ?? 0) || 0;
  if (!liquidityUsd) liquidityUsd = await fetchTokenPairsLiquidity();

  return {
    token_name: CONFIG.TOKEN_NAME,
    token_symbol: CONFIG.TOKEN_SYMBOL,
    price_usd: parseFloat(p.priceUsd || "0"),
    price_native: parseFloat(p.priceNative || "0"),
    market_cap: p.marketCap ?? p.fdv ?? 0,
    fdv: p.fdv ?? 0,
    volume_24h: p.volume?.h24 ?? 0,
    volume_6h: p.volume?.h6 ?? 0,
    volume_1h: p.volume?.h1 ?? 0,
    price_change_24h: p.priceChange?.h24 ?? 0,
    price_change_6h: p.priceChange?.h6 ?? 0,
    price_change_1h: p.priceChange?.h1 ?? 0,
    liquidity_usd: liquidityUsd,
    txns_24h_buys: p.txns?.h24?.buys ?? 0,
    txns_24h_sells: p.txns?.h24?.sells ?? 0,
    pair_url: p.url ?? "",
    dex: p.dexId ?? "orca",
  };
}

// ─── DexPaprika: real transactions (via CF Worker) ──
export async function fetchTransactions() {
  const res = await fetch(
    `${CONFIG.PROXY_URL}/transactions?pair=${CONFIG.PAIR_ADDRESS}`
  );
  const data = await res.json();
  const raw = data.transactions || [];

  return raw.map((tx) => {
    const solIsToken0 = (tx.token_0 || "").startsWith("So1111");
    const txType = solIsToken0
      ? (tx.amount_0 || 0) > 0 ? "buy" : "sell"
      : (tx.amount_1 || 0) > 0 ? "buy" : "sell";
    const tokenAmount = solIsToken0
      ? Math.abs(tx.volume_1 || 0)
      : Math.abs(tx.volume_0 || 0);
    const solAmount = solIsToken0
      ? Math.abs(tx.volume_0 || 0)
      : Math.abs(tx.volume_1 || 0);

    return {
      id: tx.id,
      tx_type: txType,
      amount: tokenAmount,
      sol_value: solAmount,
      usd_value: tx.price_1_usd ? tokenAmount * tx.price_1_usd : solAmount * (tx.price_0_usd || 0),
      wallet: tx.sender || "",
      timestamp: tx.created_at || "",
      block: tx.created_at_block_number || 0,
    };
  });
}

// ─── Buyback toplamı: RPC ile buyback cüzdanının bu pool'daki işlemlerinde harcanan SOL ───
const RPC_URL = "https://solana-rpc.publicnode.com";

function getAccountKeysFromMessage(msg, meta) {
  if (!msg) return [];
  const staticKeys = msg.staticAccountKeys ?? msg.accountKeys ?? [];
  const loaded = meta?.loadedAddresses;
  const writable = loaded?.writable ?? [];
  const readonly = loaded?.readonly ?? [];
  return [...staticKeys, ...writable, ...readonly];
}

export async function fetchBuybackTotalSol() {
  const wallet = CONFIG.BUYBACK_WALLET;
  const pair = CONFIG.PAIR_ADDRESS;
  if (!wallet || !pair) return 0;
  try {
    const sigsRes = await fetch(RPC_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "getSignaturesForAddress",
        params: [wallet, { limit: 50 }],
      }),
    });
    const sigsData = await sigsRes.json();
    const sigs = sigsData.result ?? [];
    if (sigs.length === 0) return 0;

    let totalLamports = 0;
    const batch = 5;
    for (let i = 0; i < Math.min(sigs.length, 30); i += batch) {
      const chunk = sigs.slice(i, i + batch);
      const txPromises = chunk.map((s) =>
        fetch(RPC_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            jsonrpc: "2.0",
            id: 1,
            method: "getTransaction",
            params: [
              s.signature,
              { encoding: "json", maxSupportedTransactionVersion: 0 },
            ],
          }),
        }).then((r) => r.json())
      );
      const txResults = await Promise.all(txPromises);
      for (const tr of txResults) {
        const tx = tr.result;
        if (!tx?.meta || tx.meta.err) continue;
        const msg = tx.transaction?.message;
        const accountKeys = getAccountKeysFromMessage(msg, tx.meta);
        const pairIdx = accountKeys.indexOf(pair);
        if (pairIdx === -1) continue;
        const walletIdx = accountKeys.indexOf(wallet);
        if (walletIdx === -1) continue;
        const pre = Number(tx.meta.preBalances?.[walletIdx] ?? 0);
        const post = Number(tx.meta.postBalances?.[walletIdx] ?? 0);
        const spent = pre - post;
        if (spent > 0) totalLamports += spent;
      }
    }
    return totalLamports / 1e9;
  } catch {
    return 0;
  }
}

// ─── Token supply from chain (burn sonrası güncel supply) ───
export async function fetchTokenSupply() {
  try {
    const res = await fetch(RPC_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "getTokenSupply",
        params: [CONFIG.TOKEN_CA],
      }),
    });
    const data = await res.json();
    const value = data.result?.value;
    if (!value) return null;
    const currentSupply = parseFloat(value.uiAmountString || "0") || 0;
    const initialSupply = CONFIG.INITIAL_SUPPLY;
    const burned = Math.max(0, initialSupply - currentSupply);
    const burnPercentage = initialSupply > 0 ? (burned / initialSupply) * 100 : 0;
    return { currentSupply, initialSupply, burned, burnPercentage };
  } catch {
    return null;
  }
}

// ─── Burn calculation from DexScreener (fallback when RPC supply fails) ───
export function calculateBurnData(stats) {
  if (!stats || !stats.price_usd || stats.price_usd === 0) return null;
  const currentSupply = stats.fdv / stats.price_usd;
  const initialSupply = CONFIG.INITIAL_SUPPLY;
  const burned = Math.max(0, initialSupply - currentSupply);
  const burnPercentage = initialSupply > 0 ? (burned / initialSupply) * 100 : 0;
  return { currentSupply, initialSupply, burned, burnPercentage };
}

// ─── Buyback wallet SOL balance (native SOL, getBalance) ───
const RPC_URL_BUYBACK = "https://solana-rpc.publicnode.com";

export async function fetchBuybackSolBalance() {
  try {
    const res = await fetch(RPC_URL_BUYBACK, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "getBalance",
        params: [CONFIG.BUYBACK_WALLET],
      }),
    });
    const data = await res.json();
    const lamports = Number(data.result?.value ?? 0);
    return lamports / 1e9;
  } catch {
    return 0;
  }
}

// ─── Buyback wallet token balance (RPC getTokenAccountsByOwner) ───
function parseTokenAmount(acc) {
  const data = acc?.account?.data;
  if (!data) return 0;
  const parsed = data.parsed ?? data;
  const info = parsed?.info;
  if (!info) return 0;
  const tokenAmount = info.tokenAmount ?? info.token?.amount;
  if (!tokenAmount) return 0;
  const ui = tokenAmount.uiAmountString ?? tokenAmount.uiAmount;
  if (ui != null && ui !== "") return parseFloat(ui);
  const raw = tokenAmount.amount;
  const decimals = tokenAmount.decimals ?? 6;
  if (raw == null) return 0;
  return Number(raw) / Math.pow(10, decimals);
}

export async function fetchBuybackBalance() {
  try {
    const res = await fetch(RPC_URL_BUYBACK, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "getTokenAccountsByOwner",
        params: [
          CONFIG.BUYBACK_WALLET,
          { mint: CONFIG.TOKEN_CA },
          { encoding: "jsonParsed" },
        ],
      }),
    });
    const data = await res.json();
    if (data.error) return 0;
    const accounts = data.result?.value ?? [];
    let total = 0;
    for (const acc of accounts) {
      total += parseTokenAmount(acc);
    }
    return total;
  } catch {
    return 0;
  }
}

// ─── Formatters ─────────────────────────────────────
export function formatPrice(price) {
  if (!price || price === 0) return "$0";
  if (price >= 1) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (price >= 0.01) return `$${price.toFixed(4)}`;

  const str = price.toFixed(20);
  const match = str.match(/^0\.(0+)(\d{3,4})/);
  if (match) {
    return { type: "subscript", zeros: match[1].length, digits: match[2] };
  }
  return `$${price.toFixed(8)}`;
}

export function formatPriceStr(price) {
  if (!price || price === 0) return "$0";
  if (price >= 1) return `$${price.toFixed(2)}`;
  if (price >= 0.01) return `$${price.toFixed(4)}`;
  const str = price.toFixed(20);
  const match = str.match(/^0\.(0+)(\d{3,4})/);
  if (match) return `$0.0(${match[1].length})${match[2]}`;
  return `$${price.toFixed(8)}`;
}

export function formatNumber(num) {
  if (!num && num !== 0) return "0";
  if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function formatUSD(num) {
  if (!num && num !== 0) return "$0";
  if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(1)}K`;
  return `$${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}
